<script>
  // Allocations: one page, three small tables (employees, funds, allocations).
  // All mutations go through fetch() against admin JSON endpoints; the
  // backend returns the persisted row and we splice it into local state.
  let {
    allocations = [],
    employees = [],
    funds = [],
    queries = [],
    endpoints = {
      allocations: '', employees: '', funds: '',
      allocations_query: '', allocations_export: '',
      allocations_bulk_update_status: '', allocations_save_diff: ''
    },
    csrf = ''
  } = $props();

  let rows = $state(allocations);
  let emps = $state(employees);
  let fnds = $state(funds);

  let newEmp = $state({ name: '' });
  let newFund = $state({ project: '', task: '', fund: '', cref: '' });
  let newAlloc = $state({ employee_id: '', fund_id: '', year: '', month: '', percent: '', status: '' });

  let editingId = $state(null);
  let editDraft = $state({});
  let banner = $state(null);

  // Query preview + export state
  let preview = $state(null);   // { name, label, columns, rows }
  let exporting = $state(false);

  // "Save new as allocations" modal state (status_update diff only)
  let saveDiffOpen = $state(false);
  let saveDiffStatus = $state('');
  let savingDiff = $state(false);

  // Parameter values per query, keyed by query name. Each query in the
  // registry advertises its params; we keep an editable bag per query
  // so switching between queries doesn't clobber what was typed.
  let queryParams = $state(
    Object.fromEntries(
      queries.map(q => [
        q.name,
        Object.fromEntries((q.params ?? []).map(p => [ p.name, '' ]))
      ])
    )
  );

  // Bulk status-rename state
  let statusRename = $state({ from: '', to: '' });
  let renaming = $state(false);

  // The new-allocation form doubles as a filter for the grid below:
  // anything you've typed/picked also restricts which existing rows
  // are visible. Empty fields are wildcards. resetFilters() (the
  // Reset button) clears everything in one shot.
  function matchesNumber(filter, value) {
    if (filter === '' || filter === null || filter === undefined) return true;
    if (value === null || value === undefined || value === '') return false;
    return Number(value) === Number(filter);
  }
  function matchesId(filter, value) {
    if (filter === '' || filter === null || filter === undefined) return true;
    return String(value) === String(filter);
  }
  function matchesStatus(filter, value) {
    if (!filter) return true;
    return (value ?? '').toString().toLowerCase().includes(filter.toLowerCase());
  }

  let filteredRows = $derived(
    rows.filter(r =>
      matchesId(newAlloc.employee_id, r.employee_id) &&
      matchesId(newAlloc.fund_id,     r.fund_id)     &&
      matchesNumber(newAlloc.year,    r.year)        &&
      matchesNumber(newAlloc.month,   r.month)       &&
      matchesNumber(newAlloc.percent, r.percent)     &&
      matchesStatus(newAlloc.status,  r.status)
    )
  );

  let filterActive = $derived(
    Object.values(newAlloc).some(v => v !== '' && v !== null && v !== undefined)
  );

  function resetFilters() {
    newAlloc = { employee_id: '', fund_id: '', year: '', month: '', percent: '', status: '' };
  }

  async function api(method, url, body) {
    const opts = {
      method,
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrf
      },
      credentials: 'same-origin'
    };
    if (body !== undefined) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    const res = await fetch(url, opts);

    // Never read an HTML page as success. fetch() follows redirects
    // silently, so a session that expired while this page sat open
    // turns a mutation into "302 → sign-in page → 200 text/html" —
    // and without this guard a delete would drop the row from the
    // table while the record stayed in the database.
    const contentType = res.headers.get('content-type') || '';
    if (res.redirected || contentType.includes('text/html')) {
      throw new Error('Your session has expired — reload the page and sign in again.');
    }

    if (res.status === 204) return null;
    let payload = null;
    try { payload = await res.json(); } catch { /* may have empty body */ }
    if (!res.ok) {
      // Endpoints report either a list of model errors or a single
      // human-readable string; surface whichever came back.
      const msg = (payload && payload.errors && payload.errors.join('; ')) ||
                  (payload && payload.error) ||
                  `HTTP ${res.status}`;
      throw new Error(msg);
    }
    return payload;
  }

  function flash(msg, kind = 'error') {
    banner = { kind, msg };
    setTimeout(() => { if (banner && banner.msg === msg) banner = null; }, 4000);
  }

  function empName(id) { return emps.find(e => e.id === id)?.name ?? '—'; }
  function fundLabel(id) { return fnds.find(f => f.id === id)?.label ?? '—'; }

  async function addEmployee(e) {
    e.preventDefault();
    if (!newEmp.name.trim()) return;
    try {
      const created = await api('POST', endpoints.employees, { employee: { name: newEmp.name.trim() } });
      emps = [ ...emps, created ].sort((a, b) => a.name.localeCompare(b.name));
      newEmp = { name: '' };
    } catch (err) { flash(err.message); }
  }

  async function addFund(e) {
    e.preventDefault();
    try {
      const created = await api('POST', endpoints.funds, { fund: { ...newFund } });
      fnds = [ ...fnds, created ];
      newFund = { project: '', task: '', fund: '', cref: '' };
    } catch (err) { flash(err.message); }
  }

  async function addAllocation(e) {
    e.preventDefault();
    if (!newAlloc.employee_id || !newAlloc.fund_id) {
      flash('Pick an employee and a fund.');
      return;
    }
    try {
      const created = await api('POST', endpoints.allocations, { allocation: coerce(newAlloc) });
      rows = [ created, ...rows ];
      // Intentionally keep newAlloc values so the user can add several
      // rows in a row without re-picking employee/fund/year/etc.
    } catch (err) { flash(err.message); }
  }

  function startEdit(row) {
    editingId = row.id;
    editDraft = { ...row };
  }
  function cancelEdit() { editingId = null; editDraft = {}; }

  async function saveEdit() {
    try {
      const updated = await api('PATCH', `${endpoints.allocations}/${editingId}`, { allocation: coerce(editDraft) });
      rows = rows.map(r => r.id === updated.id ? updated : r);
      cancelEdit();
    } catch (err) { flash(err.message); }
  }

  async function destroyRow(id) {
    if (!confirm('Delete this allocation?')) return;
    try {
      await api('DELETE', `${endpoints.allocations}/${id}`);
      rows = rows.filter(r => r.id !== id);
    } catch (err) { flash(err.message); }
  }

  function queryUrl(q) {
    const url = new URL(endpoints.allocations_query, window.location.origin);
    url.searchParams.set('name', q.name);
    const p = queryParams[q.name] ?? {};
    for (const [ k, v ] of Object.entries(p)) {
      if (v !== '' && v !== null && v !== undefined) url.searchParams.set(`p[${k}]`, v);
    }
    return url.pathname + url.search;
  }

  async function runQuery(q) {
    try {
      preview = await api('GET', queryUrl(q));
    } catch (err) { flash(err.message); }
  }

  async function exportPreview() {
    if (!preview) return;
    exporting = true;
    try {
      const body = { name: preview.name, p: queryParams[preview.name] ?? {} };
      const created = await api('POST', endpoints.allocations_export, body);
      banner = {
        kind: 'success',
        msg: `Created "${created.title}". `,
        link: { href: created.url, text: 'Open document' },
        share: { href: created.share_url, text: 'Share settings' }
      };
    } catch (err) {
      flash(err.message);
    } finally {
      exporting = false;
    }
  }

  // Which preview column carries the post-update value for each month:
  // the "(new)" half of a split pair, or the plain month column when
  // that month had no collision and stayed single. Mirrors what the
  // server writes so the modal can state a row count up front.
  let diffSaveColumns = $derived.by(() => {
    if (!preview) return [];
    const byMonth = new Map();
    for (const c of preview.columns) {
      const m = c.match(/^(\d{4}-\d{2})(?: \((old|new)\))?$/);
      if (!m || m[2] === 'old') continue;
      byMonth.set(m[1], c);
    }
    return [ ...byMonth.values() ];
  });

  let diffSaveCount = $derived.by(() => {
    if (!preview) return 0;
    let n = 0;
    for (const r of preview.rows) {
      if (r.project === '(total)') continue;   // server-inserted summary row
      for (const c of diffSaveColumns) {
        const v = r[c];
        if (v !== null && v !== undefined && v !== '') n++;
      }
    }
    return n;
  });

  function openSaveDiff() {
    saveDiffStatus = '';
    saveDiffOpen = true;
  }

  async function saveDiff(e) {
    e.preventDefault();
    if (!preview) return;
    savingDiff = true;
    try {
      const body = {
        name: preview.name,
        p: queryParams[preview.name] ?? {},
        status: saveDiffStatus
      };
      const res = await api('POST', endpoints.allocations_save_diff, body);
      rows = [ ...res.rows, ...rows ];
      saveDiffOpen = false;
      flash(`Created ${res.created} allocation${res.created === 1 ? '' : 's'} ` +
            `with status ${res.status ? `"${res.status}"` : '(blank)'}.`, 'success');
    } catch (err) {
      flash(err.message);
    } finally {
      savingDiff = false;
    }
  }

  async function applyStatusRename(e) {
    e.preventDefault();
    if (statusRename.from === statusRename.to) {
      flash("'From' and 'to' are the same — nothing to do.");
      return;
    }
    const label = statusRename.from === '' ? '(blank)' : `"${statusRename.from}"`;
    const target = statusRename.to === '' ? '(blank)' : `"${statusRename.to}"`;
    if (!confirm(`Change every allocation with status ${label} to ${target}?`)) return;

    renaming = true;
    try {
      const res = await api('POST', endpoints.allocations_bulk_update_status,
        { from: statusRename.from, to: statusRename.to });
      // Update the in-memory rows so the editable grid reflects the change.
      const fromValue = statusRename.from === '' ? null : statusRename.from;
      const toValue   = statusRename.to   === '' ? null : statusRename.to;
      rows = rows.map(r => r.status === fromValue ? { ...r, status: toValue } : r);
      flash(`Updated ${res.updated} row${res.updated === 1 ? '' : 's'}.`, 'success');
    } catch (err) {
      flash(err.message);
    } finally {
      renaming = false;
    }
  }

  // Coerce blank strings to null and numeric fields to numbers so the
  // server doesn't see "12" as a string and reject the numericality check.
  function coerce(row) {
    const out = { ...row };
    [ 'employee_id', 'fund_id', 'year', 'month', 'percent' ].forEach(k => {
      if (out[k] === '' || out[k] === null || out[k] === undefined) {
        out[k] = null;
      } else {
        out[k] = Number(out[k]);
      }
    });
    if (out.status === '') out.status = null;
    return out;
  }
</script>

<!-- svelte:window must live at the top level, so the modal guard is in the handler. -->
<svelte:window onkeydown={(e) => { if (e.key === 'Escape' && saveDiffOpen && !savingDiff) saveDiffOpen = false; }} />

<div class="space-y-6">
  {#if banner}
    <div class="rounded-md p-3 text-sm
                {banner.kind === 'error' ? 'bg-red-50 border border-red-200 text-red-800'
                                         : 'bg-green-50 border border-green-200 text-green-800'}">
      {banner.msg}
      {#if banner.link}
        <a class="underline mr-3" href={banner.link.href}>{banner.link.text}</a>
      {/if}
      {#if banner.share}
        <a class="underline" href={banner.share.href}>{banner.share.text}</a>
      {/if}
    </div>
  {/if}

  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <section class="bg-white border border-gray-200 rounded-md p-4">
      <h2 class="text-sm font-semibold text-gray-900 mb-2">Add employee</h2>
      <form onsubmit={addEmployee} class="flex gap-2">
        <input type="text" placeholder="Name" required bind:value={newEmp.name}
               class="flex-1 rounded-md border-gray-300 sm:text-sm" />
        <button type="submit" class="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500">Add</button>
      </form>
      <p class="mt-2 text-xs text-gray-500">{emps.length} employees</p>
    </section>

    <section class="bg-white border border-gray-200 rounded-md p-4">
      <h2 class="text-sm font-semibold text-gray-900 mb-2">Add fund</h2>
      <form onsubmit={addFund} class="grid grid-cols-2 gap-2">
        <input type="text" placeholder="Project" bind:value={newFund.project}
               class="rounded-md border-gray-300 sm:text-sm" />
        <input type="text" placeholder="Task" bind:value={newFund.task}
               class="rounded-md border-gray-300 sm:text-sm" />
        <input type="text" placeholder="Fund" bind:value={newFund.fund}
               class="rounded-md border-gray-300 sm:text-sm" />
        <input type="text" placeholder="Cref" bind:value={newFund.cref}
               class="rounded-md border-gray-300 sm:text-sm" />
        <button type="submit" class="col-span-2 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500">Add fund</button>
      </form>
      <p class="mt-2 text-xs text-gray-500">{fnds.length} funds</p>
    </section>
  </div>

  <section class="bg-white border border-gray-200 rounded-md p-4">
    <h2 class="text-sm font-semibold text-gray-900 mb-2">Bulk status rename</h2>
    <p class="text-xs text-gray-500 mb-3">
      Update every allocation whose status equals <em>from</em> so it
      matches <em>to</em>. Leave a field blank to match (or set) rows
      with no status. Acts on all allocations in one shot.
    </p>
    <form onsubmit={applyStatusRename} class="flex flex-wrap gap-2 items-end">
      <label class="text-xs text-gray-700">
        <span class="block mb-0.5">From</span>
        <input type="text" placeholder="(blank)" bind:value={statusRename.from}
               class="rounded-md border-gray-300 sm:text-sm" />
      </label>
      <label class="text-xs text-gray-700">
        <span class="block mb-0.5">To</span>
        <input type="text" placeholder="(blank)" bind:value={statusRename.to}
               class="rounded-md border-gray-300 sm:text-sm" />
      </label>
      <button type="submit" disabled={renaming}
              class="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">
        {renaming ? 'Applying…' : 'Apply'}
      </button>
    </form>
  </section>

  <section class="bg-white border border-gray-200 rounded-md p-4 space-y-3">
    <div>
      <h2 class="text-sm font-semibold text-gray-900">Queries</h2>
      <p class="text-xs text-gray-500">Run a query to preview the rows. Export creates a TSV and uploads it as a private document you can then share.</p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
      {#each queries as q}
        <div class="border border-gray-200 rounded-md p-3 space-y-2">
          <div class="flex items-center justify-between gap-2">
            <strong class="text-sm text-gray-900">{q.label}</strong>
            <button onclick={() => runQuery(q)}
                    class="rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-500">
              Run
            </button>
          </div>
          {#if q.params && q.params.length > 0}
            <div class="grid grid-cols-2 gap-2">
              {#each q.params as p}
                <label class="text-xs text-gray-700">
                  <span class="block mb-0.5">{p.label || p.name}</span>
                  <input type={p.type || 'text'}
                         bind:value={queryParams[q.name][p.name]}
                         class="w-full rounded-md border-gray-300 sm:text-sm" />
                </label>
              {/each}
            </div>
          {/if}
        </div>
      {/each}
    </div>

    {#if preview}
      <div class="flex items-center justify-between gap-3 border-t border-gray-200 pt-3">
        <p class="text-xs text-gray-600">
          Previewing <strong>{preview.label}</strong> — {preview.rows.length} row{preview.rows.length === 1 ? '' : 's'}.
        </p>
        <div class="flex items-center gap-2 shrink-0">
          <button onclick={exportPreview} disabled={exporting}
                  class="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">
            {exporting ? 'Exporting…' : 'Export to wiki as TSV'}
          </button>
          {#if preview.name === 'status_update'}
            <button onclick={openSaveDiff}
                    title="Write the post-update value of every month in range back as new allocation rows"
                    class="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              Save new as allocations
            </button>
          {/if}
        </div>
      </div>
      <div class="overflow-x-auto max-h-96 overflow-y-auto border border-gray-200 rounded-md">
        <table class="min-w-full divide-y divide-gray-200 text-sm">
          <thead class="bg-gray-50 sticky top-0 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              {#each preview.columns as c}<th class="px-3 py-2 font-mono">{c}</th>{/each}
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 font-mono">
            {#each preview.rows as r, i}
              <tr class={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {#each preview.columns as c}
                  <td class="px-3 py-1.5 whitespace-nowrap">{r[c] ?? ''}</td>
                {/each}
              </tr>
            {/each}
            {#if preview.rows.length === 0}
              <tr><td colspan={preview.columns.length} class="px-3 py-6 text-center text-sm text-gray-500">No rows.</td></tr>
            {/if}
          </tbody>
        </table>
      </div>
    {/if}
  </section>

  <section class="bg-white border border-gray-200 rounded-md overflow-hidden">
    <table class="min-w-full divide-y divide-gray-200 text-sm">
      <thead class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
        <tr>
          <th class="px-3 py-2">Employee</th>
          <th class="px-3 py-2">Fund</th>
          <th class="px-3 py-2">Year</th>
          <th class="px-3 py-2">Month</th>
          <th class="px-3 py-2">%</th>
          <th class="px-3 py-2">Status</th>
          <th class="px-3 py-2"></th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
        <!-- New row -->
        <tr class="bg-indigo-50/50">
          <td class="px-3 py-2">
            <select bind:value={newAlloc.employee_id} class="w-full rounded-md border-gray-300 sm:text-sm">
              <option value="">— pick —</option>
              {#each emps as e}<option value={e.id}>{e.name}</option>{/each}
            </select>
          </td>
          <td class="px-3 py-2">
            <select bind:value={newAlloc.fund_id} class="w-full rounded-md border-gray-300 sm:text-sm">
              <option value="">— pick —</option>
              {#each fnds as f}<option value={f.id}>{f.label}</option>{/each}
            </select>
          </td>
          <td class="px-3 py-2"><input type="number" bind:value={newAlloc.year}  class="w-20 rounded-md border-gray-300 sm:text-sm" /></td>
          <td class="px-3 py-2"><input type="number" bind:value={newAlloc.month} min="1" max="12" class="w-16 rounded-md border-gray-300 sm:text-sm" /></td>
          <td class="px-3 py-2"><input type="number" bind:value={newAlloc.percent} min="0" max="100" step="5" class="w-20 rounded-md border-gray-300 sm:text-sm" /></td>
          <td class="px-3 py-2"><input type="text"  bind:value={newAlloc.status} class="w-32 rounded-md border-gray-300 sm:text-sm" /></td>
          <td class="px-3 py-2 text-right space-x-1 whitespace-nowrap">
            <button onclick={addAllocation} class="text-xs rounded-md bg-indigo-600 px-2 py-1 text-white hover:bg-indigo-500">Add</button>
            <button onclick={resetFilters} title="Clear all filters / new-row fields"
                    class="text-xs rounded-md bg-white px-2 py-1 text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Reset</button>
          </td>
        </tr>

        {#each filteredRows as row (row.id)}
          {#if editingId === row.id}
            <tr class="bg-yellow-50">
              <td class="px-3 py-2">
                <select bind:value={editDraft.employee_id} class="w-full rounded-md border-gray-300 sm:text-sm">
                  {#each emps as e}<option value={e.id}>{e.name}</option>{/each}
                </select>
              </td>
              <td class="px-3 py-2">
                <select bind:value={editDraft.fund_id} class="w-full rounded-md border-gray-300 sm:text-sm">
                  {#each fnds as f}<option value={f.id}>{f.label}</option>{/each}
                </select>
              </td>
              <td class="px-3 py-2"><input type="number" bind:value={editDraft.year}    class="w-20 rounded-md border-gray-300 sm:text-sm" /></td>
              <td class="px-3 py-2"><input type="number" bind:value={editDraft.month}   min="1" max="12" class="w-16 rounded-md border-gray-300 sm:text-sm" /></td>
              <td class="px-3 py-2"><input type="number" bind:value={editDraft.percent} min="0" max="100" step="5" class="w-20 rounded-md border-gray-300 sm:text-sm" /></td>
              <td class="px-3 py-2"><input type="text"   bind:value={editDraft.status} class="w-32 rounded-md border-gray-300 sm:text-sm" /></td>
              <td class="px-3 py-2 text-right space-x-1">
                <button onclick={saveEdit}   class="text-xs rounded-md bg-indigo-600 px-2 py-1 text-white hover:bg-indigo-500">Save</button>
                <button onclick={cancelEdit} class="text-xs rounded-md bg-white px-2 py-1 text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Cancel</button>
              </td>
            </tr>
          {:else}
            <tr>
              <td class="px-3 py-2">{empName(row.employee_id)}</td>
              <td class="px-3 py-2">{fundLabel(row.fund_id)}</td>
              <td class="px-3 py-2">{row.year ?? ''}</td>
              <td class="px-3 py-2">{row.month ?? ''}</td>
              <td class="px-3 py-2">{row.percent ?? ''}</td>
              <td class="px-3 py-2">{row.status ?? ''}</td>
              <td class="px-3 py-2 text-right space-x-1">
                <button onclick={() => startEdit(row)} class="text-xs text-indigo-700 hover:text-indigo-900">edit</button>
                <button onclick={() => destroyRow(row.id)} class="text-xs text-red-700 hover:text-red-900">delete</button>
              </td>
            </tr>
          {/if}
        {/each}

        {#if filteredRows.length === 0}
          <tr><td colspan="7" class="px-3 py-6 text-center text-sm text-gray-500">
            {#if rows.length === 0}
              No allocations yet — add one above.
            {:else}
              No rows match the current filter. <button onclick={resetFilters} class="text-indigo-700 hover:text-indigo-900 underline">Reset</button>.
            {/if}
          </td></tr>
        {/if}
      </tbody>
    </table>
    {#if filterActive}
      <div class="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
        Filter active — showing {filteredRows.length} of {rows.length} rows.
        The same fields are also used when adding or filtering.
        <button onclick={resetFilters} class="ml-1 text-indigo-700 hover:text-indigo-900 underline">Reset</button>
      </div>
    {/if}
  </section>

  {#if saveDiffOpen && preview}
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="save-diff-title"
           class="w-full max-w-md rounded-lg bg-white shadow-xl border border-gray-200">
        <form onsubmit={saveDiff} class="p-4 space-y-3">
          <h2 id="save-diff-title" class="text-sm font-semibold text-gray-900">Save new as allocations</h2>
          <p class="text-xs text-gray-600">
            Creates <strong>{diffSaveCount}</strong> new allocation row{diffSaveCount === 1 ? '' : 's'} —
            the post-update value of every (employee × fund × month) in the range.
            Months that split into old/new use the <em>new</em> value; months that stayed a
            single column are carried over unchanged. Nothing existing is modified or deleted.
          </p>
          <label class="block text-xs text-gray-700">
            <span class="block mb-0.5">Status for the new rows</span>
            <!-- svelte-ignore a11y_autofocus -->
            <input type="text" autofocus bind:value={saveDiffStatus} placeholder="(blank)"
                   class="w-full rounded-md border-gray-300 sm:text-sm" />
          </label>
          <p class="text-xs text-gray-500">
            Leave blank to save rows with no status. If any of these cells already have a
            row with this status the save is refused, so pick a status you aren't using yet.
          </p>
          <div class="flex justify-end gap-2 pt-1">
            <button type="button" onclick={() => saveDiffOpen = false} disabled={savingDiff}
                    class="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={savingDiff || diffSaveCount === 0}
                    class="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">
              {savingDiff ? 'Saving…' : 'Save allocations'}
            </button>
          </div>
        </form>
      </div>
    </div>
  {/if}
</div>
